/* import __COLOCATED_TEMPLATE__ from './add-article-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { type Collection, type DataObject } from './types';
import { type TreeItem } from 'embercom/objects/tree-list';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { tracked } from '@glimmer/tracking';
import { debounce } from '@ember/runloop';
import type RouterService from '@ember/routing/router-service';
import { EntityType } from 'embercom/models/data/entity-types';
import type ArticleMultilingual from 'embercom/models/article-multilingual';
import type Owner from '@ember/owner';
import type Store from '@ember-data/store';

interface Args {
  item: TreeItem;
  closeArticlePicker: () => void;
  openArticlePicker: boolean;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export default class AddArticleModal extends Component<Args> {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked loading = false;
  @tracked hasLoadError = false;
  @tracked selectedArticles: Array<ArticleMultilingual> = [];
  @tracked addableArticles: Array<ArticleMultilingual> = [];
  @tracked searchTerm = '';
  @tracked currentPage = 0;
  @tracked canLoadMore = false;
  @tracked isLoadingMore = false;

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    this.fetchArticles();
  }

  @action
  async fetchArticles(isLoadMore = false) {
    if (isLoadMore) {
      this.isLoadingMore = true;
    } else {
      this.loading = true;
    }
    this.hasLoadError = false;

    try {
      let nextPage = isLoadMore ? this.currentPage + 1 : 1;
      let params: Record<string, unknown> = {
        page: nextPage,
        per_page: PAGE_SIZE,
      };

      let term = this.searchTerm.trim();
      if (term) {
        params['phrase'] = term;
        params['state'] = 'all';
      }

      let response = await this.store.query('article-multilingual', params);
      let meta = (response as any).meta;
      this.canLoadMore = meta?.next_page != null && meta.next_page > meta.page;
      this.currentPage = nextPage;

      let newArticles = response
        .toArray()
        .filter(
          (a: ArticleMultilingual) =>
            !a.helpCenterIds.includes(this.collection.helpCenterId) && !a.readOnly,
        );

      if (isLoadMore) {
        this.addableArticles = [...this.addableArticles, ...newArticles];
      } else {
        this.addableArticles = newArticles;
        this.selectedArticles = [];
      }
    } catch {
      this.hasLoadError = true;
      if (!isLoadMore) {
        this.addableArticles = [];
      }
    } finally {
      this.loading = false;
      this.isLoadingMore = false;
    }
  }

  @action
  loadMore() {
    this.fetchArticles(true);
  }

  @action
  onSearchInput(event: Event) {
    this.searchTerm = (event.target as HTMLInputElement).value;
    debounce(this, this._performSearch, SEARCH_DEBOUNCE_MS);
  }

  private _performSearch() {
    this.currentPage = 0;
    this.fetchArticles();
  }

  get collection() {
    return this.collectionDataObject.content as Collection;
  }

  get app() {
    return this.appService.app;
  }

  get locale() {
    return this.collectionDataObject.locale();
  }

  get collectionDataObject(): DataObject {
    return this.args.item.dataObject;
  }

  get showInitialLoading() {
    return this.loading && this.addableArticles.length === 0;
  }

  get showArticles() {
    return this.addableArticles.length > 0;
  }

  get showNoSearchResults() {
    return (
      !this.loading &&
      !this.hasLoadError &&
      this.addableArticles.length === 0 &&
      this.searchTerm.trim().length > 0
    );
  }

  get showNoArticles() {
    return (
      !this.loading &&
      !this.hasLoadError &&
      this.addableArticles.length === 0 &&
      this.searchTerm.trim().length === 0
    );
  }

  @action
  createArticleInCollection() {
    this.knowledgeHubService.createContent(EntityType.Article, {
      collectionId: this.collection.id,
      contentLocale: this.collectionDataObject.locale?.(),
    });
    this.args.closeArticlePicker();
  }

  get addToCollectionButtonLabel() {
    return this.selectedArticles.length
      ? this.intl.t('articles.collections.add_article.button', {
          count: this.selectedArticles.length,
        })
      : this.intl.t('articles.collections.add_article.default_button');
  }

  @action
  async addSelectedArticleToCollection() {
    this.loading = true;
    if (this.collectionDataObject.addArticlesToCollection) {
      await this.collectionDataObject.addArticlesToCollection(this.selectedArticles);
    }
    this.loading = false;
    this.args.closeArticlePicker();
  }

  get isAddToCollectionButtonDisabled() {
    return this.loading || !this.selectedArticles.length;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Articles::Site::Collections::Tree::AddArticleModal': typeof AddArticleModal;
    'articles/site/collections/tree/add-article-modal': typeof AddArticleModal;
  }
}
